import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { Logo } from "./Logo";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/I18nContext";
import { LANGS } from "../i18n/translations";
import { scrollToId } from "../lib/api";

const LangSwitcher = () => {
  const { lang, setLang } = useI18n();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        data-testid="lang-switcher"
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1.5 text-sm text-smoke hover:text-ink transition-colors duration-300 px-2 py-1"
        aria-label="Choisir la langue"
      >
        <Globe size={15} strokeWidth={1.5} />
        <span className="uppercase tracking-wider">{lang}</span>
      </button>
      {open && (
        <div data-testid="lang-menu" className="absolute right-0 top-full mt-2 rounded-2xl bg-white shadow-[0_16px_40px_-16px_rgba(26,29,26,0.25)] border border-linen py-2 min-w-[130px] z-50">
          {LANGS.map((l) => (
            <button
              key={l.code}
              data-testid={`lang-option-${l.code}`}
              onClick={() => { setLang(l.code); setOpen(false); }}
              className={`w-full text-left px-4 py-2 text-sm transition-colors duration-200 ${lang === l.code ? "text-sagedark font-medium" : "text-smoke hover:text-ink"}`}
            >
              {l.label} — {l.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const Navbar = () => {
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const LINKS = [
    { id: "manifeste", label: t("nav.manifeste") },
    { id: "modules", label: t("nav.modules") },
    { id: "tarifs", label: t("nav.tarifs") },
    { id: "contact", label: t("nav.contact") },
  ];

  const go = (id) => {
    setOpen(false);
    if (window.location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(id), 350);
    } else {
      scrollToId(id);
    }
  };

  return (
    <header data-testid="main-navbar" className="fixed top-0 inset-x-0 z-50 border-b border-white/40 bg-white/70 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-[72px] flex items-center justify-between">
        <Link to="/" aria-label="Accueil VAV"><Logo /></Link>
        <nav className="hidden md:flex items-center gap-10">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="text-sm tracking-wide text-smoke hover:text-ink transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
          <Link data-testid="nav-link-cabinet" to="/le-cabinet" className="text-sm tracking-wide text-smoke hover:text-ink transition-colors duration-300">
            {t("nav.cabinet")}
          </Link>
        </nav>
        <div className="flex items-center gap-3">
          <LangSwitcher />
          {user?.role === "admin" && (
            <Link
              data-testid="nav-admin-link"
              to="/admin"
              className="hidden md:inline-flex text-sm text-smoke hover:text-ink transition-colors duration-300"
            >
              Back-office
            </Link>
          )}
          <Link
            data-testid="nav-client-cta"
            to={user ? "/espace-client" : "/connexion"}
            className="hidden md:inline-flex items-center rounded-full bg-ink text-paper text-sm px-6 py-2.5 hover:bg-sagedark transition-colors duration-300"
          >
            {user ? t("nav.myspace") : t("nav.espace")}
          </Link>
          <button
            data-testid="nav-mobile-toggle"
            className="md:hidden p-2 text-ink"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
          </button>
        </div>
      </div>
      {open && (
        <div data-testid="nav-mobile-menu" className="md:hidden bg-white/95 backdrop-blur-xl border-t border-linen px-6 py-6 flex flex-col gap-5">
          {LINKS.map((l) => (
            <button key={l.id} data-testid={`nav-mobile-link-${l.id}`} onClick={() => go(l.id)} className="text-left text-base text-ink">
              {l.label}
            </button>
          ))}
          <Link data-testid="nav-mobile-client-cta" to={user ? "/espace-client" : "/connexion"} onClick={() => setOpen(false)} className="rounded-full bg-ink text-paper text-sm px-6 py-3 text-center">
            {user ? t("nav.myspace") : t("nav.espace")}
          </Link>
        </div>
      )}
    </header>
  );
};
